import React, { useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import { filterProductCategory } from 'actions/filters'
import { productsLoadProducts, productsLoadStock } from 'actions/products'
import { marketCategory } from 'actions/trackingKeys'
import { basketUpdateProducts } from 'routes/Menu/actions/basket'
import { getBasketOrderDetails, getBasketOrderId, getBasketSaveRequired } from 'selectors/basket'
import {
  getCategoriesForNavBar,
  getProductsForMarket,
  getProductsLoadError,
  getProductsStockLoadError,
} from 'selectors/products'
import { getBasket, getProductCategories } from 'selectors/root'
import { getBasketSaveError, getBasketSavePending } from 'selectors/status'

import { ALL_PRODUCTS_CATEGORY_NAME, ALL_PRODUCTS_CATEGORY_ID } from '../../constants/categories'
import type { NavigationCategory, NavigationCategories } from '../../types/navigationCategory'
import type { ProductCategory } from '../../types/productCategory'
import type { Products } from '../../types/products'
import { MarketPresentation } from './Market.presentation'

type Props = {
  ageVerified: boolean
  canEditOrder: boolean
  showContextualSides: boolean
  toggleAgeVerificationPopUp: () => void
}

const Market = (props: Props) => {
  const dispatch = useDispatch()

  const [filteredProducts, setFilteredProducts] = useState<Products | null>(null)
  const [isOrderSummaryOpen, setIsOrderSummaryOpen] = useState<boolean>(false)
  const [trackingCategoryTitle, setTrackingCategoryTitle] = useState<string>(
    ALL_PRODUCTS_CATEGORY_NAME,
  )
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(ALL_PRODUCTS_CATEGORY_ID)

  const basket = useSelector(getBasket)
  const categoriesForNavBar: NavigationCategories = useSelector(getCategoriesForNavBar)
  const products = useSelector(getProductsForMarket)
  const productsCategories = useSelector(getProductCategories)
  const productsLoadError = useSelector(getProductsLoadError)
  const productsStockLoadError = useSelector(getProductsStockLoadError)
  const basketSaveError = useSelector(getBasketSaveError)
  const basketSaveRequired = useSelector(getBasketSaveRequired)
  const basketSavePending = useSelector(getBasketSavePending)
  const order = useSelector(getBasketOrderDetails)
  const orderId = useSelector(getBasketOrderId)
  const marketLoadError = productsLoadError || productsStockLoadError

  const toggleOrderSummary = () => {
    setIsOrderSummaryOpen(!isOrderSummaryOpen)
  }

  const onOrderSave = () => {
    dispatch(basketUpdateProducts({ isOrderConfirmation: true, shouldRedirectToMyGousto: true }))
  }

  const onRetryMarketLoad = () => {
    const postcode = order?.getIn(['shippingAddress', 'postcode'])
    const periodId = order?.getIn(['period', 'id'])

    if (!postcode || !periodId) return

    setFilteredProducts(null)
    setTrackingCategoryTitle(ALL_PRODUCTS_CATEGORY_NAME)
    setSelectedCategoryId(ALL_PRODUCTS_CATEGORY_ID)

    dispatch(productsLoadStock(true))
    dispatch(productsLoadProducts(postcode, periodId, { reload: true }))
  }

  const getFilteredProducts = (categoryId: string) => {
    const selectedFilterCategory: NavigationCategory = categoriesForNavBar[categoryId]

    if (!Object.keys(products).length || !selectedFilterCategory) return

    let chosenCategoryProducts: Products = {}

    setSelectedCategoryId(categoryId)
    setTrackingCategoryTitle(selectedFilterCategory.label)

    if (categoryId === ALL_PRODUCTS_CATEGORY_ID) {
      chosenCategoryProducts = products
    } else {
      Object.keys(products).forEach((productId) => {
        const productCategories = products[productId].categories

        if (productCategories) {
          productCategories.forEach((category: ProductCategory) => {
            const productProps = products[productId]

            if (categoryId === category.id) {
              const product = { [productProps.id]: { ...productProps } }
              chosenCategoryProducts = { ...chosenCategoryProducts, ...product }
            }
          })
        }
      })
    }

    const numOfProducts = Object.keys(chosenCategoryProducts).length

    dispatch(
      filterProductCategory(
        marketCategory,
        'clicked',
        'secondary_action',
        selectedFilterCategory.label,
        numOfProducts,
        categoryId,
      ),
    )

    setFilteredProducts(chosenCategoryProducts)
  }

  const { ageVerified, canEditOrder, showContextualSides, toggleAgeVerificationPopUp } = props

  return (
    <MarketPresentation
      ageVerified={ageVerified}
      canEditOrder={canEditOrder}
      basket={basket}
      categoriesForNavBar={categoriesForNavBar}
      filteredProducts={filteredProducts}
      getFilteredProducts={getFilteredProducts}
      isOrderSummaryOpen={isOrderSummaryOpen}
      toggleOrderSummary={toggleOrderSummary}
      onOrderSave={onOrderSave}
      onRetryMarketLoad={onRetryMarketLoad}
      orderId={orderId}
      products={products}
      productsCategories={productsCategories}
      productsLoadError={marketLoadError}
      showCategoryTags={selectedCategoryId === ALL_PRODUCTS_CATEGORY_ID}
      saveError={basketSaveError}
      saveRequired={basketSaveRequired}
      saving={basketSavePending}
      showContextualSidesModal={showContextualSides}
      showOrderConfirmationReceipt={!!order}
      toggleAgeVerificationPopUp={toggleAgeVerificationPopUp}
      trackingCategory={trackingCategoryTitle}
    />
  )
}

export { Market }
